import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PaymentType, renderAmount, Transaction, TransactionStatus } from "../../../../models/Transactions";
import { getTransactionDisplayAmount, prepareTransactionsForDisplay } from "../../../../utils/transactionDisplay";
import Loader from "../../components/Loader";
import { useInView } from "react-intersection-observer";
import { useTheme } from "../../../../hooks/useTheme";
import { useWallet } from "../../../../hooks/useWallet";
import { useNavigate } from "react-router-dom";
import { dateStringToLocaleDateWithoutYear, formatAmount, getFlagEmoji, localeFormatDate } from "../../../../utils/Utils";
import { formatSentStatsAmount } from "../../../../utils/walletStatsFormat";
import { Button, ButtonSize } from "../../../../components/Button";
import { ButtonVariant } from "../../../../config/colors";
import { OPAAvatar } from "../../../../components/OPAAvatar";
import { BulkTypes } from "../../../../models/BulkPayment";

interface WalletTransactionsProps {
  transactions: Transaction[];
  isLoading: boolean;
  error?: string | null;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isSearchActive?: boolean;
  isSearching?: boolean;
  searchError?: string;
  showTitle?: boolean;
}

type FilterType = 'all' | 'sent' | 'received';

const WalletTransactions: React.FC<WalletTransactionsProps> = ({
  transactions,
  isLoading,
  error,
  hasMore = false,
  onLoadMore,
  isSearchActive = false,
  isSearching = false,
  searchError = '',
  showTitle = true,
}) => {
  const { currencyType, stats } = useWallet();
  const { ref, inView } = useInView();
  const isMobile = useTheme();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const navigate = useNavigate();
  const baseTransactions = useMemo(
    () => prepareTransactionsForDisplay(transactions),
    [transactions],
  );

  // Filter transactions before grouping
  const filteredTransactions = useMemo(() => {
    return baseTransactions.filter(transaction => {
        // Hide transactions marked with hidden
        if (transaction.hidden) return false;
        
        if (activeFilter === 'all') return true;
        const isSent = transaction.amount < 0;
        return activeFilter === 'sent' ? isSent : !isSent;
    });
  }, [baseTransactions, activeFilter]);

  // Group transactions by date
  const groupedTransactions = useMemo(() => {
    const groups: { [key: string]: Transaction[] } = {};
    
    filteredTransactions.forEach(transaction => {
      const date = new Date(transaction.time);
      // Format the date as "Month Year" (e.g., "March 2024")
      const dateKey = date.toLocaleString(localeFormatDate, { month: 'long', year: 'numeric' });

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(transaction);
    });

    // Sort the transactions within each group by date (newest first)
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    });

    return groups;
  }, [filteredTransactions]);

  useEffect(() => {
    if (inView && hasMore && onLoadMore && !isLoading && !isSearching) {
      onLoadMore();
    }
  }, [inView, hasMore, onLoadMore, isLoading, isSearching]);

  const getImgTransactionBulk = (transaction: Transaction) => {
    let imgInside = null;

    if(transaction.bulk?.type === BulkTypes.city) {
        imgInside = transaction.bulk?.geoZap?.addressType === 'country' ? (
            transaction.bulk.geoZap.countryImgUrl ? (
                <img src={transaction.bulk.geoZap.countryImgUrl} className="w-full h-full object-cover" />
            ) : (
                <span className="text-2xl">{getFlagEmoji(transaction.bulk?.geoZap?.countryCode ?? '')}</span>
            )
        ) : (
            <span className="material-symbols-outlined">location_city</span>
        )
    }
    else if(transaction.bulk?.type === BulkTypes.distanceFromMyself) {
        imgInside = <span className="material-symbols-outlined">location_on</span>;
    }
    else if(transaction.bulk?.imgUrl) {
        imgInside = <img src={transaction.bulk.imgUrl} className="w-full h-full object-cover" />;
    }

    return imgInside ? (
        <div className="shrink-0">
            <OPAAvatar
                size={33}
                borderWidth={0}
                borderColor="var(--brand-bg-2)"
            >
                <div className="flex items-center justify-center w-full h-full bg-brand-bg-4">
                    {imgInside}
                </div>
            </OPAAvatar>
        </div>
    ) : null;
  }

  const showTransactionDetailLeft = useCallback((transaction: Transaction) => {
    let isSend = transaction.amount < 0;

    if(transaction.type === PaymentType.bulk)
    {
        const nbToSend = transaction.bulk?.nbToSend ?? 0;

        return <div className="flex flex-row items-center gap-2">
            {getImgTransactionBulk(transaction)}
            <div>
                <div className="text-sm font-semibold">
                    {transaction.bulk?.name}
                </div>
                <div className="text-xs text-brand-color-2">
                    {formatAmount(nbToSend)} recipient{nbToSend <= 1 ? '' : 's'}
                </div>
            </div>
        </div>
    }

    if(!isSend && transaction.sender && transaction.sender.internalUser)
    {
        return <div className="flex flex-row items-center gap-2">
            <div className="shrink-0">
                <OPAAvatar
                    imageUrl={transaction.sender?.internalUser?.image}
                    size={33}
                    borderWidth={0}
                    borderColor="var(--brand-bg-2)"
                />
            </div>
            <div>
                <div className="text-xs font-semibold text-brand-color-2">
                    {dateStringToLocaleDateWithoutYear(transaction.time)}
                </div>
                <div className="text-sm font-semibold">
                    {transaction.customTitle ?? transaction.sender.internalUser.name}
                </div>
            </div>
        </div>
    }
    else if(isSend && transaction.recipient && transaction.recipient.internalUser)
    {
        return <div className="flex flex-row items-center gap-2">
            <div className="shrink-0">
                <OPAAvatar
                    imageUrl={transaction.recipient?.internalUser?.image}
                    size={33}
                    borderWidth={0}
                    borderColor="var(--brand-bg-2)"
                />
            </div>
            <div>
                <div className="text-xs font-semibold text-brand-color-2">
                    {dateStringToLocaleDateWithoutYear(transaction.time)}
                </div>
                <div className="text-sm font-semibold">
                    {transaction.customTitle ?? transaction.recipient.internalUser.name}
                </div>
            </div>
        </div>
    }
    else {
        const isPending = transaction.status === TransactionStatus.PENDING;

        let defaultTitle: string;
        if (isPending) {
            defaultTitle = isSend ? 'Sending' : 'Receiving';
        } else {
            defaultTitle = isSend ? 'Sent' : 'Received';
        }

        let suffixTitle: string = '';
        if (transaction.btcTxId) {
            suffixTitle = 'On-chain';
        }

        if (suffixTitle !== '') {
            isSend ? defaultTitle += ` to ${suffixTitle}` : defaultTitle += ` from ${suffixTitle}`;
        }

        const title = transaction.customTitle ?? defaultTitle;
        
        return <>
            <div className="text-xs font-semibold text-brand-color-2">
                {dateStringToLocaleDateWithoutYear(transaction.time)}
            </div>
            <div className={`text-sm font-semibold ${isSend ? '' : 'text-brand-green'}`}>{title}</div>
        </>
    }
  }, []);

  if(error) {
    return <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
        <p className="text-center text-base opacity-50">
            {error}
        </p>
    </div>;
  }

  return (
    <div className="container mx-auto">
        {showTitle && (
            <div className="text-xl font-bold text-center">
                Transactions
            </div>
        )}

        <div className="mt-4">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
                {(['all', 'sent', 'received'] as FilterType[]).map((filter) => {
                    const isSelected = activeFilter === filter;
                    let buttonVariant = isSelected ? ButtonVariant.inverted : ButtonVariant.unselected;
                    const filterLabel = filter.charAt(0).toUpperCase() + filter.slice(1);

                    if(filter === 'received' && isSelected)
                        buttonVariant = ButtonVariant.green;

                    return <Button 
                        key={filter} 
                        variant={buttonVariant} 
                        size={ButtonSize.medium}
                        onClick={() => {
                            setActiveFilter(filter);
                        }}
                        className=""
                    >
                        <span>
                            {filterLabel}
                            <span className="text-xs">
                                {isSelected && filter === 'sent' && typeof stats?.sent === 'number' && stats.sent > 0
                                    ? ` (${formatSentStatsAmount(stats.sent)} SATS)`
                                    : ''}
                            </span>
                            <span className="text-xs">
                                {isSelected && filter === 'received' && typeof stats?.received === 'number' && stats.received > 0
                                    ? ` (${formatSentStatsAmount(stats.received)} SATS)`
                                    : ''}
                            </span>
                        </span>
                    </Button>
                })}
            </div>
        </div>

        <div className="mt-4 flex-1">
            {searchError && (
                <div className="text-center text-sm text-brand-red mb-3">
                    {searchError}
                </div>
            )}

            {Object.entries(groupedTransactions).map(([date, dateTransactions]) => (
                <div key={date} className="mb-4">
                    <div className="flex flex-row items-center gap-2">
                        <div className="font-semibold">
                            {date}
                        </div>
                        
                    </div>
                    <div className="flex flex-col mt-2">
                        {dateTransactions.map((transaction) => {
                            const isSend = transaction.amount < 0;

                            return <div 
                                    key={transaction.paymentHash} 
                                    onClick={() => navigate(`/home/wallet/tx/${transaction.paymentHash}`)}
                                    className="cursor-pointer border-b border-brand-border-color"
                                >
                                <div className="rounded-md hover:bg-brand-bg-3 py-2 px-4">
                                    <div className="flex flex-row justify-between items-center gap-4">
                                        <div className="flex flex-row items-center gap-2">
                                            {
                                                !transaction.read && (
                                                    <div className="w-2 h-2 bg-brand-red rounded-full" />
                                                )
                                            }
                                            {
                                                transaction.status === TransactionStatus.PENDING && (
                                                    <Loader xsmall />
                                                )
                                            }
                                            <div className="flex flex-col">
                                                {showTransactionDetailLeft(transaction)}
                                            </div>
                                        </div>
                                        <div className={`shrink-0 text-xl font-semibold ${isSend ? '' : 'text-brand-green'}`}>
                                            {
                                                getTransactionDisplayAmount(transaction) !== 0 && <div>{renderAmount(transaction, currencyType)}</div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>;
                        })}
                    </div>
                </div>
            ))}

            {isSearching && (
                <div className="flex justify-center items-center py-4">
                    <Loader small />
                </div>
            )}

            {filteredTransactions.length === 0 && !isLoading && !isSearching && (
                <div className="flex flex-col items-center justify-center h-full mt-8">
                    <div className="text-center text-base opacity-50">
                        {isSearchActive ? 'No matching transactions found' : `No ${activeFilter !== 'all' ? activeFilter : ''} transactions found`}
                    </div>
                </div>
            )}
        </div>

        <div
            className="h-20 flex items-center justify-center"
        >
            {isLoading && !isSearchActive && <div className="flex justify-center items-center py-4"><Loader small /></div>}
            {hasMore && <div ref={ref} className="h-5" />}
        </div>
    </div>
  );
}

export default WalletTransactions;